import uuidv4 from "uuid-random";
import Logger from "../../clients/logger";
import ConfigClient from "../../clients/configClient";
import { StandardError } from "../../types";
import { FinsembleNativeWindowObject } from "./types";

export default class AssimilationHelper {
	appAssets: Record<string, any>;

	manifest: Record<string, any>;

	blacklist: Record<string, any>;

	whitelist: Record<string, any>;

	/**
	 *Creates an instance of AssimilationHelper. This class houses the manifest and any functions that may be needed across classes
	 * @param {Object} config
	 * @param {Object} config.appAssets
	 * @param {Object} config.manifest
	 * @memberof AssimilationHelper
	 */
	constructor(config: Record<string, any>) {
		this.appAssets = config.appAssets; // This is a list of all hooked windows'
		this.manifest = config.manifest;
		this.blacklist = this.manifest.blacklist.map((x: string) => x.toUpperCase());
		this.whitelist = this.manifest.whitelist.map((x: string) => x.toUpperCase());
	}

	/**
	 * Should the window be hooked
	 *
	 * @param {*} windowWrap
	 * @returns
	 * @memberof AssimilationHelper
	 */
	canHookWindow(windowWrap: Record<string, any>): boolean {
		const onlySpawnedWindows = this.manifest.onlySpawned;
		if (onlySpawnedWindows && !windowWrap.descriptor) {
			return false;
		}
		if (onlySpawnedWindows && windowWrap.descriptor && !this.checkBlacklist(windowWrap)) {
			Logger.system.debug("Assimilation.canHookWindow: true");
			return true;
		}

		if (this.checkBlacklist(windowWrap)) {
			return false;
		}
		if (this.checkWhitelist(windowWrap)) {
			return true;
		}
		return this.whitelist.length === 0;
	}

	// See if a window is in the finsemble config
	/**
	 *
	 *
	 * @param {*} nativeWindowObject - A file path
	 * @param {*} cb
	 * @memberof AssimilationHelper
	 */
	executableInConfig(nativeWindowObject: Record<string, any>, cb: (err: StandardError, found: boolean) => unknown) {
		if (!nativeWindowObject.executablePath) return cb("not found", false);
		const windowFileName = nativeWindowObject.executablePath.replace(/^.*[\\\/]/, "").toUpperCase();
		ConfigClient.getValue({ field: "finsemble.components" }, (err, config) => {
			const componentKeys = Object.keys(config);
			for (let i = 0; i < componentKeys.length; i++) {
				const component = config[componentKeys[i]];
				if (component.window && component.window.path) {
					const componentFileName = component.window.path.replace(/^.*[\\\/]/, "").toUpperCase();
					if (nativeWindowObject.executablePath && componentFileName === windowFileName) {
						return cb(null, true);
					}
				}
			}
			return this.executableInAssets(nativeWindowObject, cb);
		});
	}

	executableInAssets(nativeWindowObject: FinsembleNativeWindowObject, cb: Function) {
		for (let i = 0; i < this.appAssets.length; i++) {
			const assetFile = this.appAssets[i].target.toUpperCase();
			const windowFileName = nativeWindowObject.executablePath.replace(/^.*[\\\/]/, "").toUpperCase();
			if (assetFile === windowFileName) return cb(null, true);
		}
		return cb(null, false);
	}

	/**
	 *Check to see if a window is in the blacklist
	 *
	 * @param {*} windowObject
	 * @returns
	 * @memberof AssimilationHelper
	 */
	checkBlacklist(windowObject: Record<string, any>): boolean {
		if (!windowObject.windowName) return false;
		if (this.blacklist.length > 0 && this.blacklist.includes(windowObject.windowName.toUpperCase())) {
			return true;
		}
		for (let i = 0; i < this.blacklist.length; i++) {
			const re = new RegExp(`^.*\\${this.blacklist[i]}$`);
			if (re.test(windowObject.windowName.toUpperCase())) {
				return false;
			}
			if (windowObject.executablePath && re.test(windowObject.executablePath.toUpperCase())) {
				return false;
			}
		}
		return false;
	}

	/**
	 *Check to see if the window is in the whitelist
	 *
	 * @param {*} windowObject
	 * @returns
	 * @memberof AssimilationHelper
	 */
	checkWhitelist(windowObject: Record<string, any>): boolean {
		if (!windowObject.windowName) return false;
		for (let i = 0; i < this.whitelist.length; i++) {
			const re = new RegExp(`^.*\\${this.whitelist[i]}$`);
			if (re.test(windowObject.windowName.toUpperCase())) {
				return true;
			}
			if (windowObject.executablePath && re.test(windowObject.executablePath.toUpperCase())) {
				return true;
			}
		}
		return false;
	}

	/**
	 *Helper for creating an action for socket communication
	 *
	 * @param {*} json
	 * @returns
	 * @memberof AssimilationHelper
	 */
	prepareAction(json: Record<string, any>): string {
		const ref = uuidv4();
		json.ref = ref;
		const output = JSON.stringify(json);

		json.expiry = Date.now() + 3000; // 3 seconds to wait
		return output;
	}

	/**
	 *Helper for finding an app asset by alias
	 *
	 * @param {*} alias
	 * @returns
	 * @memberof AssimilationHelper
	 */
	findAssetByAlias(alias: string) {
		if (!Array.isArray(this.appAssets)) {
			return null;
		}
		for (let i = 0; i < this.appAssets.length; i++) {
			if (this.appAssets[i].alias === alias || this.appAssets[i].path === alias) {
				return this.appAssets[i];
			}
		}
		return null;
	}
}
